// Model review checks: design warnings that hold for any database. They complement validation,
// which reports models that are wrong; these report models that are probably not what you want.
// Like the rest of modelviewer they read only the logical model, never database details (`physical`),
// except that cascade paths, like the deletion view, guess which database a model was made for.
import { deleteImpact, guessDatabase, tableOf, type DeleteStep } from '../delete/impact';
import { rootOfPath } from '../model/logical';
import type { ComplexSpec, EntitySpec, ModelSpec, OwnedSpec, PropertySpec } from '../model/schema';

export type LintSeverity = 'warning' | 'info';

export interface LintFinding {
  rule: string;
  severity: LintSeverity;
  /** Entity key or owned path. */
  entity?: string;
  property?: string;
  /** Index into model.relationships. */
  relationship?: number;
  message: string;
  hint?: string;
}

export interface LintRule {
  id: string;
  severity: LintSeverity;
  title: string;
  description: string;
  check: (model: ModelSpec) => LintFinding[];
}

type Container = EntitySpec | OwnedSpec | ComplexSpec;

/** Every property of the model with its entity path (owned types and complex properties included). */
function* allProperties(model: ModelSpec): Generator<{ entity: string; property: string; spec: PropertySpec }> {
  function* walk(path: string, c: Container, prefix: string): Generator<{ entity: string; property: string; spec: PropertySpec }> {
    for (const [name, spec] of Object.entries(c.properties)) yield { entity: path, property: `${prefix}${name}`, spec };
    for (const [name, cx] of Object.entries(c.complex ?? {})) yield* walk(path, cx, `${prefix}${name}.`);
    if ('owned' in c) for (const [nav, o] of Object.entries(c.owned ?? {})) yield* walk(`${path}.${nav}`, o, '');
  }
  for (const [key, e] of Object.entries(model.entities)) yield* walk(key, e, '');
}

const stringMaxLength: LintRule = {
  id: 'string-max-length',
  severity: 'info',
  title: 'Text without a maximum length',
  description: 'A string without a maximum length becomes an unlimited text column (nvarchar(max) on SQL Server), which cannot be indexed and costs more to store and compare.',
  check: (model) => {
    const out: LintFinding[] = [];
    for (const { entity, property, spec } of allProperties(model)) {
      if (spec.type !== 'string' || spec.collection || spec.maxLength !== undefined) continue;
      out.push({
        rule: 'string-max-length',
        severity: 'info',
        entity,
        property,
        message: `${entity}.${property} has no maximum length.`,
        hint: 'Set maxLength if the text has a natural limit (names, codes, emails).',
      });
    }
    return out;
  },
};

const decimalPrecision: LintRule = {
  id: 'decimal-precision',
  severity: 'warning',
  title: 'Decimal without precision',
  description: 'EF Core warns about decimals without precision and scale: the database default (often 18,2) may silently round or truncate values.',
  check: (model) => {
    const out: LintFinding[] = [];
    for (const { entity, property, spec } of allProperties(model)) {
      if (spec.type !== 'decimal' || spec.collection || spec.precision !== undefined) continue;
      out.push({
        rule: 'decimal-precision',
        severity: 'warning',
        entity,
        property,
        message: `${entity}.${property} is a decimal without precision.`,
        hint: 'Set precision and scale, for example 18 and 2 for money.',
      });
    }
    return out;
  },
};

/** `a, b and c` */
const listed = (items: string[]) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`);

const cascadePaths: LintRule = {
  id: 'cascade-paths',
  severity: 'warning',
  title: 'Several cascade delete paths',
  description:
    'When deleting one row reaches a table along more than one path of cascading actions (cascade or set null), or around a cycle, SQL Server refuses to create the foreign keys. ' +
    "EF Core's client-only behaviors are no action in the database and do not count. PostgreSQL allows it, so models made for PostgreSQL are not checked.",
  check: (model) => {
    // The deletion view's rules: SQL Server's unless the model was made for PostgreSQL.
    if (guessDatabase(model) === 'postgres') return [];
    const rels = model.relationships;
    // Each problem is found from every row that leads to it; keep the one found closest to it.
    const found = new Map<string, { finding: LintFinding; length: number }>();
    const keep = (id: string, finding: LintFinding, length: number) => {
      const was = found.get(id);
      if (!was || length < was.length) found.set(id, { finding, length });
    };
    const owned = rels.map((r) => r.principal).filter((p) => !model.entities[p]);
    for (const start of [...new Set([...Object.keys(model.entities), ...owned])]) {
      const impact = deleteImpact(model, start, 'sqlServer');
      for (const conflict of impact.conflicts) {
        const table = tableOf(model, conflict.entity);
        const converging: DeleteStep[] = [];
        for (const step of conflict.steps.map((i) => impact.steps[i]!)) {
          const passed = [start, ...step.path.slice(0, -1).map((r) => rels[r]!.dependent)];
          const from = passed.findIndex((n) => tableOf(model, n) === table);
          if (from < 0) {
            converging.push(step);
            continue;
          }
          const r = rels[step.relationship]!;
          const cycle = step.path.slice(from);
          if (cycle.length === 1) {
            keep(
              `cycle|${cycle[0]}`,
              {
                rule: 'cascade-paths',
                severity: 'warning',
                entity: r.dependent,
                relationship: step.relationship,
                message:
                  step.effect === 'delete'
                    ? `Deleting a ${r.principal} cascades to other ${r.dependent} rows through ${r.foreignKey.join(', ')}.`
                    : `Deleting a ${r.principal} sets ${r.foreignKey.join(', ')} to null in other ${r.dependent} rows.`,
                hint: 'SQL Server refuses a foreign key that cascades or sets null in its own table. Use restrict or no action, and delete or clear the rows in code.',
              },
              0,
            );
            continue;
          }
          const names = [passed[from]!, ...cycle.map((i) => rels[i]!.dependent)];
          keep(
            `cycle|${[...cycle].sort((a, b) => a - b).join(',')}`,
            {
              rule: 'cascade-paths',
              severity: 'warning',
              entity: passed[from],
              relationship: step.relationship,
              message: `Cascading actions go round in a cycle: ${names.join(' → ')}.`,
              hint: 'SQL Server refuses this. Make one relationship in the cycle restrict or no action.',
            },
            0,
          );
        }
        if (converging.length < 2) continue;
        // The foreign key each path comes in by tells paths apart, also two from one table.
        const paths = converging.map((s) => {
          const names = [start, ...s.path.map((i) => rels[i]!.dependent)];
          return `${names.join(' → ')} (${rels[s.relationship]!.foreignKey.join(', ')})`;
        });
        const last = converging.map((s) => s.relationship).sort((a, b) => a - b);
        keep(
          `paths|${table}|${last.join(',')}`,
          {
            rule: 'cascade-paths',
            severity: 'warning',
            entity: conflict.entity,
            relationship: converging.at(-1)!.relationship,
            message: `Deleting a ${start} reaches ${conflict.entity} along ${converging.length} paths of cascade or set null: ${listed(paths)}.`,
            hint: 'SQL Server refuses this. Make all but one of these relationships restrict or no action; EF Core can still delete or clear the rows it has loaded (client cascade, client set null).',
          },
          converging.reduce((n, s) => n + s.path.length, 0),
        );
      }
    }
    return [...found.values()].map((f) => f.finding);
  },
};

const WIDE = 30;

const wideEntity: LintRule = {
  id: 'wide-entity',
  severity: 'info',
  title: 'Very wide entity',
  description: `Tables with more than ${WIDE} columns often hold several concepts. A separate entity, or a JSON column for data read as a whole, can make them smaller.`,
  check: (model) => {
    // Columns in the entity's own table: its properties, and those of complex types and single owned
    // types stored in the same table. Collections, JSON and owned types in their own table do not count.
    const inTable = (c: Container): number => {
      let n = Object.keys(c.properties).length;
      for (const x of Object.values(c.complex ?? {})) if (!x.collection && !x.mapping?.json) n += inTable(x);
      if ('owned' in c) for (const o of Object.values(c.owned ?? {})) if (!o.collection && !o.mapping?.json && !o.mapping?.table) n += inTable(o) - o.ownerKey.length;
      return n;
    };
    return Object.entries(model.entities)
      .map(([entity, e]) => [entity, inTable(e)] as const)
      .filter(([, n]) => n > WIDE)
      .map(([entity, n]) => ({
        rule: 'wide-entity',
        severity: 'info' as const,
        entity,
        message: `${entity} has ${n} columns in its table.`,
        hint: 'Split off a separate entity for a group that is read or changed on its own, or store data read as a whole as JSON.',
      }));
  },
};

const isolatedEntity: LintRule = {
  id: 'isolated-entity',
  severity: 'info',
  title: 'Entity with no relationships',
  description: 'An entity that is not related to any other is fine for lookup data and logs, but is often a missing relationship.',
  check: (model) => {
    const linked = new Set<string>();
    for (const r of model.relationships) {
      linked.add(rootOfPath(model, r.dependent).root);
      linked.add(rootOfPath(model, r.principal).root);
    }
    for (const [key, e] of Object.entries(model.entities)) {
      if (e.base) {
        linked.add(key);
        linked.add(e.base);
      }
    }
    if (Object.keys(model.entities).length < 2) return [];
    return Object.entries(model.entities)
      .filter(([key, e]) => !linked.has(key) && !e.keyless)
      .map(([entity]) => ({
        rule: 'isolated-entity',
        severity: 'info' as const,
        entity,
        message: `${entity} is not related to any other entity.`,
        hint: 'Check whether a relationship is missing.',
      }));
  },
};

const sharedTable: LintRule = {
  id: 'shared-table',
  severity: 'warning',
  title: 'Two entities in one table',
  description: 'Two entities mapped to the same table must share their key through a one-to-one relationship (table splitting); otherwise the model is rejected.',
  check: (model) => {
    const byTable = new Map<string, string[]>();
    for (const [key, e] of Object.entries(model.entities)) {
      if (e.base || !e.mapping?.table) continue;
      const table = `${e.mapping.schema ?? ''}.${e.mapping.table}`.toLowerCase();
      byTable.set(table, [...(byTable.get(table) ?? []), key]);
    }
    const out: LintFinding[] = [];
    for (const [, keys] of byTable) {
      if (keys.length < 2) continue;
      const linked = (a: string, b: string) =>
        model.relationships.some((r) => r.cardinality === 'one-to-one' && ((r.dependent === a && r.principal === b) || (r.dependent === b && r.principal === a)));
      for (let i = 1; i < keys.length; i++) {
        if (keys.slice(0, i).some((k) => linked(k, keys[i]!))) continue;
        out.push({
          rule: 'shared-table',
          severity: 'warning',
          entity: keys[i],
          message: `${keys[i]} and ${keys[0]} are both mapped to table ${model.entities[keys[0]!]!.mapping!.table}.`,
          hint: 'Give one of them another table, or link them one-to-one on their keys (table splitting).',
        });
      }
    }
    return out;
  },
};

export const LINT_RULES: LintRule[] = [decimalPrecision, cascadePaths, sharedTable, stringMaxLength, wideEntity, isolatedEntity];

/** Runs the review checks (all, or those listed in `rules`). */
export function lintModel(model: ModelSpec, options: { rules?: string[] } = {}): LintFinding[] {
  const rules = options.rules ? LINT_RULES.filter((r) => options.rules!.includes(r.id)) : LINT_RULES;
  return rules.flatMap((r) => r.check(model));
}
